import React, { useEffect, useState, Suspense, lazy, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiImage } from "react-icons/fi";
import ProductInfo from "../../components/pageProps/productDetails/ProductInfo";
import ProductsOnSale from "../../components/pageProps/productDetails/ProductsOnSale";
import Breadcrumbs from "../../components/pageProps/Breadcrumbs";
import { loadProduct, paginationItems6 } from "../../constants/products";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Autoplay, Navigation } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "react-medium-image-zoom/dist/styles.css";
import PinchZoomImage from "./PinchZoomImage";

SwiperCore.use([Autoplay, Navigation]);
const Zoom = lazy(() => import("react-medium-image-zoom"));

const ProductDetails = () => {
  const location = useLocation();
  const params = useParams();

  const [productInfo, setProductInfo] = useState({});
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.item) {
      setProductInfo(location.state.item);
    } else if (params._id) {
      setProductInfo({ codigo: params._id, Descripcion: "" });
    }
  }, [location, params._id]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productInfo.codigo) return;
      setLoading(true);
      try {
        const cod = productInfo.codigo.replace(/\//g, "|");
        await loadProduct(cod);
        const fetchedProduct = paginationItems6[0];
        setProduct(fetchedProduct);
        setActiveImage(fetchedProduct?.img || "");
      } catch (error) {
        console.error("Error al cargar el producto:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productInfo]);

  const handleImageClick = (imgUrl) => setActiveImage(imgUrl);

  const productImages = useMemo(() => {
    const gallery = (product?.prod_imagenes || [])
      .map((imgObj) => imgObj.rimagen)
      .filter(Boolean);

    if (product?.img && !gallery.includes(product.img)) {
      return [product.img, ...gallery];
    }

    return gallery;
  }, [product]);

  const activeIndex = productImages.findIndex((img) => img === activeImage);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-12">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="h-[560px] animate-pulse rounded-[28px] border border-gray-200 bg-gradient-to-br from-gray-100 to-gray-50" />
          <div className="space-y-4">
            <div className="h-8 w-40 animate-pulse rounded-full bg-gray-100" />
            <div className="h-12 w-full animate-pulse rounded-2xl bg-gray-100" />
            <div className="h-40 w-full animate-pulse rounded-[28px] bg-gray-100" />
            <div className="h-60 w-full animate-pulse rounded-[28px] bg-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return <div className="p-10 text-center">Producto no encontrado</div>;
  }

  return (
    <div className="w-full border-b border-gray-200 bg-gradient-to-b from-slate-50 via-white to-white">
      <div className="mx-auto max-w-[1280px] px-4 py-8 lg:py-10">
        <Breadcrumbs prevLocation="Catalogo" title={product.Descripcion || "Producto"} />

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:items-start">
          <section className="rounded-[32px] border border-gray-200 bg-white p-4 shadow-sm md:p-6">
            <div className="grid gap-5 xl:grid-cols-[108px_minmax(0,1fr)]">
              <div className="order-2 xl:order-1">
                {productImages.length > 1 ? (
                  productImages.length <= 5 ? (
                    <div className="flex gap-3 overflow-x-auto pb-1 xl:flex-col">
                      {productImages.map((img, index) => (
                        <button
                          key={`${img}-${index}`}
                          type="button"
                          onClick={() => handleImageClick(img)}
                          className={`flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border p-2 transition ${
                            activeImage === img
                              ? "border-gray-900 bg-gray-50 shadow-sm"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <img
                            src={img}
                            alt={`Vista ${index + 1}`}
                            loading="lazy"
                            className="h-full w-full object-contain"
                          />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="relative">
                      <Swiper
                        direction="vertical"
                        modules={[Autoplay, Navigation]}
                        slidesPerView={5}
                        spaceBetween={12}
                        allowTouchMove
                        navigation={{
                          prevEl: ".productThumbPrev",
                          nextEl: ".productThumbNext",
                        }}
                        className="h-[520px]"
                      >
                        {productImages.map((img, index) => (
                          <SwiperSlide key={`${img}-${index}`}>
                            <button
                              type="button"
                              onClick={() => handleImageClick(img)}
                              className={`flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border p-2 transition ${
                                activeImage === img
                                  ? "border-gray-900 bg-gray-50 shadow-sm"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <img
                                src={img}
                                alt={`Vista ${index + 1}`}
                                loading="lazy"
                                className="h-full w-full object-contain"
                              />
                            </button>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      <button
                        type="button"
                        className="productThumbPrev absolute left-1/2 top-0 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm"
                      >
                        <FiChevronLeft className="rotate-90" />
                      </button>
                      <button
                        type="button"
                        className="productThumbNext absolute bottom-0 left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm"
                      >
                        <FiChevronRight className="rotate-90" />
                      </button>
                    </div>
                  )
                ) : null}
              </div>

              <div className="order-1 xl:order-2">
                <div className="relative flex min-h-[420px] items-center justify-center overflow-hidden rounded-[28px] border border-gray-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6 md:min-h-[560px]">
                  {activeImage ? (
                    <Suspense fallback={<div>Cargando zoom...</div>}>
                      <Zoom>
                        <PinchZoomImage
                          className="max-h-[520px] w-full object-contain"
                          src={activeImage}
                          alt={product?.Descripcion}
                        />
                      </Zoom>
                    </Suspense>
                  ) : (
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <FiImage className="text-4xl" />
                      <span className="text-sm">Sin imagen disponible</span>
                    </div>
                  )}

                  {productImages.length > 1 && (
                    <div className="absolute bottom-4 right-4 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-600 shadow-sm">
                      {activeIndex >= 0 ? `${activeIndex + 1} / ${productImages.length}` : `${productImages.length} im\u00e1genes`}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {
              console.log("cc ",product.productos_desc_x_cant)
              // {product.productos_desc_x_cant.length > 0 && (
                // <div className="absolute bottom-4 right-4 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-600 shadow-sm">
                //   {}
                // </div>
              // )}
            }
            {product.productos_desc_x_cant?.length > 0 && (
              <table className="w-full border-collapse border border-gray-300 mt-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Cantidad Desde</th>
                    <th className="border p-2 text-left">Descuento</th>
                    <th className="border p-2 text-left">Fecha Hasta</th>
                  </tr>
                </thead>

                <tbody>
                  {product.productos_desc_x_cant.map((item, index) => (
                    <tr key={index} className="small">
                      <td className="border p-2 text-right">
                        {item.cantidad_desde}
                      </td>
                      <td className="border p-2 text-right">
                        {item.descuento}%
                      </td>
                      <td className="border p-2 text-right">
                        {new Date(item.fecha_hasta).toLocaleDateString("es-AR")}
                      </td>                      
                    </tr>
                  ))}
                </tbody>
              </table>
            )}            

          </section>

          <aside className="space-y-6">
            <ProductInfo productInfo={product} />

            {product?.datos_adicionales && (
              <section className="rounded-[32px] border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                    Informacion Extra
                  </p>
                  <h2 className="mt-2 text-2xl font-bold text-gray-900">Detalles Adicionales</h2>
                </div>
                <div
                  className="prose prose-sm max-w-none text-gray-600"
                  dangerouslySetInnerHTML={{ __html: product.datos_adicionales }}
                />
              </section>
            )}
          </aside>
        </div>

        {product?.productos_relacionados?.length > 0 && (
          <div className="mt-12">
            <ProductsOnSale relatedProducts={product.productos_relacionados} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;
